/**
 * Scroll-in animation wrappers for the landing page.
 *
 * The homepage is a static file, so each wrapper is written into it at its
 * starting point — `opacity: 0` and a small offset, inline — before anything
 * knows the visitor's motion preference. Reduced motion is therefore the
 * stylesheet's call, not React's: every wrapper carries `data-motion-reveal`,
 * and `motionUtils.css` holds it at rest under `prefers-reduced-motion:
 * reduce`, from first paint.
 *
 * So a wrapper renders the same element whatever the preference. Swapping to a
 * plain `div` on `useReducedMotion()` is what blanked the page (#1094): React
 * hydrates over the static markup without patching its attributes, and the
 * inline `opacity: 0` stayed for good.
 */
import { motion } from "./motion";
import "./motionUtils.css";

const EASE = [0.25, 0.1, 0.25, 1];
const DURATION = 0.45;

const MotionWrapper = ({ children, className, delay = 0, initial, animate, ...rest }) => (
  <motion.div
    className={className}
    data-motion-reveal=""
    initial={initial}
    whileInView={animate}
    viewport={{ once: true, margin: "-48px" }}
    transition={{ duration: DURATION, delay, ease: EASE }}
    {...rest}
  >
    {children}
  </motion.div>
);

export const FadeUp = (props) => (
  <MotionWrapper {...props} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} />
);

export const FadeIn = (props) => (
  <MotionWrapper {...props} initial={{ opacity: 0 }} animate={{ opacity: 1 }} />
);

export const FadeLeft = (props) => (
  <MotionWrapper {...props} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} />
);

export const FadeRight = (props) => (
  <MotionWrapper {...props} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} />
);

export const ScaleIn = (props) => (
  <MotionWrapper
    {...props}
    initial={{ opacity: 0, scale: 0.97 }}
    animate={{ opacity: 1, scale: 1 }}
  />
);
